import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography
} from "@mui/material";
import { api, useGetAdminsQuery } from "../../state/api";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import CustomColumnMenu from "../../components/DataGridCustomColumnMenu";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import { useDispatch } from "react-redux";
import { deleteAdminAction } from "../../redux/actions/adminAction";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PasswordIcon from "@mui/icons-material/Password";
import axios from "axios";
import * as yup from "yup";
import { usePostUpdateStatusMutation } from "../../state/api";
import PermissionChecker from "../../utils/permissionChecker";

const Admin = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data, error, isLoading } = useGetAdminsQuery();
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(20);

  useEffect(
    () => {
      if (data) {
        setRowData(data);
      }
    },
    [data]
  );

  // const handleEdit = async (adminId) => {
  //   try {
  //     const response = await dispatch(getAdminProfileActionFn(adminId));
  //     if (response && response.success) {
  //       const adminDetails = response.response;
  //       navigate("/admin/edit", { state: { adminDetails } });
  //     } else {
  //       console.log("Failled tp fetch admin details");
  //       toast.error("Failled tp fetch admin details");
  //     }
  //   } catch (error) {
  //     console.log("Error fetching admin details", error);
  //     toast.error("Error fetching admin details");
  //   }
  // };

  const handleEdit = async adminId => {
    try {
      navigate(`/admin/edit/${adminId}`);
    } catch (error) {
      console.log("Error navigating to edit", error);
      toast.error("Error navigating to edit");
    }
  };

  const handleDelete = async adminId => {
    try {
      const success = await dispatch(deleteAdminAction(adminId));
      if (success) {
        toast.success("Admin deleted successfully");
        dispatch(api.util.invalidateTags(["Admins"]));
      } else {
        toast.error("Failed to delete admin");
      }
    } catch (error) {
      console.error("Error deleting admin:", error);
    }
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const compareAdminOldPassword = async value => {
    try {
      const response = await axios.post(
        `/admin/checkOldPassword?adminId=${_id}`,
        {
          oldPassword: value
        }
      );
      return response.data.success;
    } catch (error) {
      return false;
    }
  };

  const validationSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .required("Old Password is required")
      .test("is-unique", "Old password doesn't match", async function(value) {
        try {
          const success = await compareAdminOldPassword(value);
          console.log("success:", success);
          return success;
        } catch (error) {
          console.error("Error:", error);
          return false;
        }
      }),
    newPassword: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(15, "Password must be less than 15 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
      )
      .notOneOf(
        [yup.ref("oldPassword")],
        "New password must be different from old password"
      )
      .required("New Password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password")
  });

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [_id, set_id] = useState("");

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }));
    // Clear the error when the user starts typing in the field
    setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
  };

  const handleBlur = async e => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErrors(prevErrors => ({ ...prevErrors, [fieldName]: "" }));
    } catch (error) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error.message
      }));
    }
  };

  const [updateStatus] = usePostUpdateStatusMutation();

  const handleStatus = async (Id, isActive) => {
    const tag = "Admin";
    try {
      const response = await updateStatus({ Id, tag, isActive });
      // console.log(response);
      if (response.data.success) {
        setRowData(prevData =>
          prevData.map(
            row => (row._id === Id ? { ...row, isActive: !isActive } : row)
          )
        );
        toast.success("Status updated successfully");
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const changePassword = async _id => {
    setModalOpen(true);
    set_id(_id);
  };

  const handlePasswordChange = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      const response = await axios.put(`/admin/changePassword?adminId=${_id}`, {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });
      toast.success("Password changed successfully");
      setModalOpen(false);
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach(validationError => {
          validationErrors[validationError.path] = validationError.message;
        });
        setErrors(validationErrors);
      } else {
        console.error("Error changing password:", error);
        toast.error("Failed to change password");
      }
    }
  };

  const removeAdmin = adminId => {
    Swal.fire({
      title: "Confirm Removal",
      text: "Are you sure you want to remove this ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!"
    }).then(result => {
      if (result.isConfirmed) {
        handleDelete(adminId);
      }
    });
  };

  const navigate = useNavigate();

  const navigateCreate = () => {
    navigate("/admin/create");
  };

  const columns = [
    // {
    //   field: "_id",
    //   headerName: "ID",
    //   flex: 1,
    // },
    {
      field: "username",
      headerName: "Username",
      flex: 0.5
    },
    {
      field: "fullname",
      headerName: "Full Name",
      flex: 0.5
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1
    },
    {
      field: "mobileNumber",
      headerName: "Phone Number",
      flex: 0.5
      // renderCell: (params) => {
      //   return params.value.replace(/^(\d{3})(\d{3})(\d{4})/, "($1)$2-$3");
      // },
    },
    {
      field: "isActive",
      headerName: "IsActive?",
      flex: 0.4,
      renderCell: params =>
        <div>
          <Switch
            checked={params.value}
            onChange={() => handleStatus(params.row._id, params.row.isActive)}
            inputProps={{ "aria-label": "toggle status" }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "green",
                "&:hover": {
                  backgroundColor: "rgba(0, 128, 0, 0.50)"
                }
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "green"
              },
              "& .MuiSwitch-switchBase": {
                color: "red",
                "&:hover": {
                  backgroundColor: "rgba(255, 0, 0, 0.5)"
                }
              },
              "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                backgroundColor: "red"
              }
            }}
            color="default"
          />
          {params.value ? "T" : "F"}
        </div>
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.5
    },
    {
      field: "createdAt",
      headerName: "CreatedAt",
      flex: 0.5,
      renderCell: params => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      }
    },
    {
      field: "updatedAt",
      headerName: "UpdatedAt",
      flex: 0.5,
      renderCell: params => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      }
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      renderCell: params =>
        <div>
          <PermissionChecker requiredPermissions={["EDIT_ADMIN"]}>
            <IconButton color="info" onClick={() => handleEdit(params.row._id)}>
              <EditIcon size="large" />
            </IconButton>
          </PermissionChecker>
          <IconButton
            variant="contained"
            color="#4caf50"
            onClick={() => changePassword(params.row._id)}
          >
            <PasswordIcon size="large" />
          </IconButton>
          <PermissionChecker requiredPermissions={["DELETE_MOVIE"]}>
            <IconButton
              variant="contained"
              color="secondary"
              onClick={() => removeAdmin(params.row._id)}
            >
              <DeleteIcon size="small" sx={{ color: "red" }} />
            </IconButton>
          </PermissionChecker>
        </div>
    }
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="1rem"
      >
        <Header title="ADMIN MANAGEMENT" />
        <Button
          variant="contained"
          endIcon={<AddCircleIcon />}
          onClick={navigateCreate}
        >
          Add Admin
        </Button>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none"
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none"
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none"
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none"
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`
          }
        }}
      >
        <DataGrid
          // key={rowData.length}
          loading={isLoading || !rowData}
          getRowId={row => row._id}
          rows={rowData || []}
          columns={columns}
          pagination
          pageSize={pageSize}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          components={{
            ColumnMenu: CustomColumnMenu
          }}
        />
      </Box>

      {/* Password Change Model Box */}
      <Dialog open={isModalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            label="Old Password"
            type="password"
            name="oldPassword"
            value={formData.oldPassword}
            onBlur={handleBlur}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          {errors.oldPassword &&
            <Grid item>
              <Typography color="error">
                {errors.oldPassword}
              </Typography>
            </Grid>}{" "}
          <TextField
            label="New Password"
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onBlur={handleBlur}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          {errors.newPassword &&
            <Grid item>
              <Typography color="error">
                {errors.newPassword}
              </Typography>
            </Grid>}{" "}
          <TextField
            label="Confirm Password"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onBlur={handleBlur}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          {errors.confirmPassword &&
            <Grid item>
              <Typography color="error">
                {errors.confirmPassword}
              </Typography>
            </Grid>}{" "}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={handlePasswordChange} color="success">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default Admin;
